import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  PenSquare, CalendarDays, MessageSquare, BarChart3, Ear, Megaphone,
  CheckCheck, Users, Share2, TrendingUp, Heart, Eye, Repeat2, AtSign,
  ChevronRight, Zap, Activity, Clock, Sparkles, Target, Calendar, Link2, Users2,
  BookOpen,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { PostManager } from "@/components/Posts/PostManager";
import { PostsList } from "@/components/Posts/PostsList";
import { CommentsInboxView } from "./CommentsInboxView";
import { EnhancedContentCalendar } from "@/components/Calendar/EnhancedContentCalendar";
import { SocialDMsView } from "@/components/SocialDMs/SocialDMsView";
import { AnalyticsView } from "@/components/Analytics/AnalyticsView";
import { IntelligenceView } from "@/components/Intelligence/IntelligenceView";
import { CampaignManager } from "@/components/Campaigns/CampaignManager";
import { ApprovalsView } from "@/components/Approvals/ApprovalsView";
import { SocialAccountsManager } from "@/components/SocialAccounts/SocialAccountsManager";
import { AudienceDemographicsView } from "./AudienceDemographicsView";
import { CompetitorBenchmarkView } from "./CompetitorBenchmarkView";
import { BulkSchedulerView } from "./BulkSchedulerView";
import { LinkInBioBuilder } from "./LinkInBioBuilder";
import { TeamPerformanceReport } from "./TeamPerformanceReport";
import { ContentLibraryView } from "./ContentLibraryView";
import { PostPerformanceView } from "./PostPerformanceView";
import { formatDistanceToNow } from "date-fns";

type HubTab =
  | "overview"
  | "compose"
  | "scheduled"
  | "calendar"
  | "inbox"
  | "analytics"
  | "listening"
  | "campaigns"
  | "approvals"
  | "accounts"
  | "demographics"
  | "competitors"
  | "bulk"
  | "bio"
  | "team"
  | "library"
  | "postperf";

const platformIcons: Record<string, React.ElementType> = {
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  facebook: Facebook,
  threads: AtSign,
};

const platformColors: Record<string, string> = {
  instagram: "from-purple-500 to-pink-500",
  twitter: "from-slate-800 to-slate-900",
  linkedin: "from-blue-600 to-blue-700",
  facebook: "from-blue-500 to-blue-600",
  threads: "from-slate-800 to-slate-900",
};

const NAV_ITEMS: { id: HubTab; label: string; icon: React.ElementType; badge?: string }[] = [
  { id: "overview",   label: "Overview",         icon: Activity },
  { id: "compose",    label: "Compose",           icon: PenSquare },
  { id: "scheduled",  label: "Scheduled",         icon: Clock },
  { id: "calendar",   label: "Content Calendar",  icon: CalendarDays },
  { id: "inbox",      label: "Unified Inbox",     icon: MessageSquare },
  { id: "analytics",  label: "Analytics",         icon: BarChart3 },
  { id: "listening",  label: "Social Listening",  icon: Ear },
  { id: "campaigns",  label: "Campaigns",         icon: Megaphone },
  { id: "approvals",  label: "Approvals",         icon: CheckCheck },
  { id: "accounts",      label: "Connected Accounts",   icon: Users },
  { id: "demographics",  label: "Audience",             icon: Users2 },
  { id: "competitors",   label: "Competitors",          icon: Target },
  { id: "bulk",          label: "Bulk Scheduler",       icon: Calendar },
  { id: "bio",           label: "Link in Bio",          icon: Link2 },
  { id: "team",          label: "Team Performance",     icon: Users },
  { id: "library",       label: "Content Library",      icon: BookOpen },
  { id: "postperf",      label: "Post Performance",     icon: TrendingUp },
];

function OverviewTab({ orgId }: { orgId: string | null }) {
  const qc = useQueryClient();

  // Realtime: invalidate overview queries when social_accounts or posts change
  useEffect(() => {
    const ch = supabase.channel("social-hub-overview")
      .on("postgres_changes", { event: "*", schema: "public", table: "social_accounts" },
        () => qc.invalidateQueries({ queryKey: ["social-accounts-hub"] }))
      .on("postgres_changes", { event: "*", schema: "public", table: "posts" }, () => {
        qc.invalidateQueries({ queryKey: ["recent-posts-hub"] });
        qc.invalidateQueries({ queryKey: ["scheduled-count"] });
        qc.invalidateQueries({ queryKey: ["pending-approvals-count"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc]);

  const { data: accounts = [] } = useQuery({
    queryKey: ["social-accounts-hub", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      let q = supabase.from("social_accounts").select("*").eq("user_id", user.id).eq("is_connected", true);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      return (data ?? []) as any[];
    },
  });

  const { data: recentPosts = [] } = useQuery({
    queryKey: ["recent-posts-hub", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      let q = supabase
        .from("posts")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(5);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      return (data ?? []) as any[];
    },
  });

  const { data: pendingCount = 0 } = useQuery({
    queryKey: ["pending-approvals-count", orgId],
    queryFn: async () => {
      let q = supabase.from("posts").select("id", { count: "exact", head: true }).eq("status", "pending_approval");
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { count } = await (q as any);
      return count ?? 0;
    },
  });

  const { data: scheduledCount = 0 } = useQuery({
    queryKey: ["scheduled-count", orgId],
    queryFn: async () => {
      let q = supabase.from("posts").select("id", { count: "exact", head: true }).eq("status", "scheduled");
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { count } = await (q as any);
      return count ?? 0;
    },
  });

  const totalFollowers = accounts.reduce((sum: number, a: any) => sum + (a.follower_count ?? 0), 0);

  return (
    <div className="space-y-6">
      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Connected Accounts", value: accounts.length, icon: Share2, color: "text-blue-500" },
          { label: "Total Followers", value: totalFollowers.toLocaleString(), icon: Users, color: "text-purple-500" },
          { label: "Scheduled Posts", value: scheduledCount, icon: Clock, color: "text-amber-500" },
          { label: "Pending Approvals", value: pendingCount, icon: CheckCheck, color: "text-rose-500" },
        ].map(stat => (
          <Card key={stat.label} className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
              </div>
              <stat.icon className={cn("w-5 h-5 mt-0.5", stat.color)} />
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connected platforms */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Share2 className="w-4 h-4 text-primary" />
            Connected Platforms
          </h3>
          {accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No accounts connected yet.</p>
          ) : (
            <div className="space-y-3">
              {accounts.map((acc: any) => {
                const Icon = platformIcons[acc.platform] ?? AtSign;
                const gradient = platformColors[acc.platform] ?? "from-slate-500 to-slate-600";
                const isExpired = acc.token_expires_at ? new Date(acc.token_expires_at) < new Date() : false;
                return (
                  <div key={acc.id} className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center text-white shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate capitalize">{acc.platform}</p>
                      <p className="text-xs text-muted-foreground truncate">@{acc.account_handle}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold">{(acc.follower_count ?? 0).toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">followers</p>
                    </div>
                    {isExpired && (
                      <Badge variant="destructive" className="text-[10px]">Expired</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Recent activity */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" />
            Recent Posts
          </h3>
          {recentPosts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No posts yet. Start composing!</p>
          ) : (
            <div className="space-y-3">
              {recentPosts.map((post: any) => (
                <div key={post.id} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm line-clamp-2">{post.content}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge
                        variant={
                          post.status === "published" ? "default" :
                          post.status === "scheduled" ? "outline" :
                          post.status === "pending_approval" ? "secondary" : "secondary"
                        }
                        className="text-[10px] capitalize"
                      >
                        {post.status?.replace(/_/g, " ")}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Quick actions */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Create Post",       icon: PenSquare,   href: "compose" },
            { label: "Schedule Content",  icon: CalendarDays, href: "calendar" },
            { label: "View Analytics",    icon: TrendingUp,  href: "analytics" },
            { label: "Check Inbox",       icon: MessageSquare, href: "inbox" },
          ].map(action => (
            <div
              key={action.label}
              className="flex items-center gap-2 p-3 rounded-lg border border-border hover:bg-accent/50 cursor-pointer transition-colors"
            >
              <action.icon className="w-4 h-4 text-primary shrink-0" />
              <span className="text-sm font-medium">{action.label}</span>
              <ChevronRight className="w-3 h-3 text-muted-foreground ml-auto" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export const SocialHubView = () => {
  const [activeTab, setActiveTab] = useState<HubTab>("overview");
  const [inboxSubTab, setInboxSubTab] = useState<"dms" | "comments">("dms");
  const [composeInitialContent, setComposeInitialContent] = useState<string | undefined>();
  const { orgId } = useOrgPermissions();

  const handleUseInCompose = (content: string) => {
    setComposeInitialContent(content);
    setActiveTab("compose");
  };

  const navClass = (tab: HubTab) =>
    cn(
      "flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
      activeTab === tab
        ? "bg-primary text-primary-foreground shadow-sm"
        : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
    );

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return <OverviewTab orgId={orgId} />;
      case "compose":
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold">Compose</h2>
              <p className="text-sm text-muted-foreground">Create and publish content across all your platforms</p>
            </div>
            <PostManager
              initialContent={composeInitialContent}
              onInitialContentConsumed={() => setComposeInitialContent(undefined)}
            />
          </div>
        );
      case "scheduled":
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold">Scheduled Posts</h2>
              <p className="text-sm text-muted-foreground">View and manage your upcoming content</p>
            </div>
            <PostsList />
          </div>
        );
      case "calendar":
        return <EnhancedContentCalendar onSchedulePost={() => setActiveTab("compose")} />;
      case "inbox":
        return (
          <div className="space-y-6">
            <div className="flex gap-2 border-b pb-2">
              <button
                onClick={() => setInboxSubTab("dms")}
                className={`px-4 py-1.5 text-sm rounded-t font-medium transition-colors ${inboxSubTab === "dms" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                Direct Messages
              </button>
              <button
                onClick={() => setInboxSubTab("comments")}
                className={`px-4 py-1.5 text-sm rounded-t font-medium transition-colors ${inboxSubTab === "comments" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                Comments
              </button>
            </div>
            {inboxSubTab === "dms" ? <SocialDMsView /> : <CommentsInboxView />}
          </div>
        );
      case "analytics":
        return <AnalyticsView />;
      case "listening":
        return <IntelligenceView />;
      case "campaigns":
        return <CampaignManager />;
      case "approvals":
        return <ApprovalsView />;
      case "accounts":
        return <SocialAccountsManager />;
      case "demographics":
        return <AudienceDemographicsView />;
      case "competitors":
        return <CompetitorBenchmarkView />;
      case "bulk":
        return <BulkSchedulerView />;
      case "bio":
        return <LinkInBioBuilder />;
      case "team":
        return <TeamPerformanceReport />;
      case "library":
        return <ContentLibraryView onUseInCompose={handleUseInCompose} />;
      case "postperf":
        return <PostPerformanceView />;
    }
  };

  return (
    <div className="flex gap-6 min-h-full">
      {/* Sidebar nav */}
      <aside className="w-56 shrink-0">
        <div className="sticky top-0 space-y-1">
          <div className="flex items-center gap-2 px-3 py-3 mb-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow">
              <Sparkles className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <p className="text-sm font-bold leading-none">Social Hub</p>
              <p className="text-[10px] text-muted-foreground leading-none mt-0.5">All-in-one</p>
            </div>
          </div>

          <nav className="space-y-0.5">
            {NAV_ITEMS.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={navClass(item.id)}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{item.label}</span>
                {item.badge && (
                  <Badge className="ml-auto text-[10px] h-4 px-1">{item.badge}</Badge>
                )}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {renderContent()}
      </main>
    </div>
  );
};
